import DotBtnItem from 'Components/Atoms/DotBtnItem/DotBtnItem';
import EditButtons from 'Components/Atoms/EditButtons/EditButtons';
import { Arrow } from 'Components/Atoms/ExpandableItems/Arrow';
import TextItem from 'Components/Atoms/ExpandableItems/TextItem';
import TextItemInfo from 'Components/Atoms/ExpandableItems/TextItemInfo';
import TextItemLabel from 'Components/Atoms/ExpandableItems/TextItemLabel';
import Loader from 'Components/Atoms/Loader/Loader';
import RowItem from 'Components/Atoms/Row/RowItem';
import RowItemInfo from 'Components/Atoms/Row/RowItemInfo';
import RowItemTitle from 'Components/Atoms/Row/RowItemTitle';
import Text from 'Components/Atoms/Text/Text';
import Field from 'Pages/Dashboard/Settings/FieldsManagement/Fields/FieldTypes/Field';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { convertTimestampToDateAndTime } from 'Services/Date/Date';
import { convertMimeToExtension, fileTypesAsImage, fileTypesWithoutPreview } from 'Services/File/FileService';
import { getAccessTokenFromLocal } from 'Services/helpers';
import { FieldType } from 'Store/FieldManagement/PropTypes';
import { deleteFileById, patchFileById } from 'Store/Modules/Files/Actions';
import { getFilesByIdAsObject } from 'Store/Modules/Files/Selectors';
import { StoreState } from 'Store/Reducers';
import { API_URL } from 'Store/Shared/Middleware/API/Constants';
import styles from './File.module.sass';
import ModalHeader from 'Components/Molecules/Modals/ModalHeader/ModalHeader';
import Title from 'Components/Atoms/Title/Title';
import Container from 'Components/Atoms/Container/Container';
import ModalFooter from 'Components/Molecules/Modals/ModalFooter/ModalFooter';
import ButtonGroup from 'Components/Molecules/ButtonGroup/ButtonGroup';
import Modal from 'Components/Molecules/Modals/Modal/Modal';
import Button from 'Components/Atoms/Button/Button';

const File = ({
    file,
    ...props
}: FileProps) => {
    const [state, setState] = useState({
        isOpen: false,
        isDeleted: false,
        isLoading: false,
        isEdit: false,
        isPreviewing: false
    });

    const [data, setData] = useState({
        name: file.name,
        blobUrl: ''
    });

    useEffect(() => {
        return () => {
            window.URL.revokeObjectURL(data.blobUrl);
        }
    }, [data.blobUrl]);

    useEffect(() => {
        setData({
            name: file.name,
            blobUrl: ''
        })
    }, [file]);

    const handleEdit = () => setState((state) => ({...state, isEdit: !state.isEdit, isOpen: true}));
    const handleOpen = () => setState((state) => ({...state, isOpen: !state.isOpen}));

    const handleCancel = () => {
        setData({...data, name: file.name})
        setState((state) => ({...state, isEdit: !state.isEdit, isOpen: true}));
    };
    const handleSave = () => {
        props.patchFileById(file.id, {name: data.name});

        setState({...state, isEdit: false});
    }

    const handleDelete = async () => {
        setState((state) => ({...state, isLoading: true}));
        await props.deleteFileById(file.id);
        setState((state) => ({...state, isLoading: false}));
    };

    const handleFileFetch = async () => {
        const accessToken = getAccessTokenFromLocal();

        return fetch(`${API_URL}/api/v2/files/${file.id}/download`, {
            headers: {
                Authorization: `Bearer ${accessToken}`
            }
        })
        .then(response => response.blob());
    };

    const fetchUrl = async () => {
        setState((state) => ({...state, isLoading: true}));
        let url = data.blobUrl;
        if (!url) {
            const response = await handleFileFetch();
            const blob = new Blob([response], {type: file.type});
            url = window.URL.createObjectURL(blob);
            setData({...data, blobUrl: url});
        }

        return url;
    }

    const handlePreview = async () => {
        setState((state) => ({...state, isPreviewing: true}));
        await fetchUrl();

        setState((state) => ({...state, isLoading: false}));
    }   

    const handleDownload = async () => {
        const url = await fetchUrl();

        const a = document.createElement('a');
        a.href = url;
        a.download = file.name + convertMimeToExtension(file.type);
        document.body.appendChild(a);
        a.click();
        a.remove();

        setState({...state, isLoading: false});
    }

    return <div className={`expandable manage ${state.isOpen ? 'open' : ''} ${styles['file']}`}>
        <div className={'expandable-item'}>
            <div className={'expandable-item__arrow'} onClick={handleOpen}>
                <Arrow />
            </div>
            <div className={'expandable-item__wrapper'}>
                <div className={`expandable-item__name ${styles['file__name']}`} onClick={handlePreview}>
                    <Text>
                        { file.name }{ convertMimeToExtension(file.type) }
                    </Text>
                </div>
                <TextItem hasBorder>
                    <TextItemLabel>Geüpload door:</TextItemLabel>
                    <TextItemInfo>{file?.created_by}</TextItemInfo>
                </TextItem>
                <TextItem>
                    <TextItemLabel>Geüpload op:</TextItemLabel>
                    <TextItemInfo>{convertTimestampToDateAndTime(file?.created_at.date)}</TextItemInfo>
                </TextItem>
                <div className={'expandable-item__toolbox'}>
                    { state.isLoading && <Loader size='medium' /> }
                    { !state.isLoading && <EditButtons
                        onSave={handleSave}
                        onCancel={handleCancel}
                        onDelete={handleDelete}
                        isLoading={state.isLoading}
                        deleteDescription={'Dit bestand zal volledig uit het systeem verwijderd worden.'}
                        isEdit={state.isEdit}
                        isDropdown
                    >
                        <DotBtnItem name={'Bekijken'} onClick={handlePreview} />
                        <DotBtnItem name={'Hernoemen'} onClick={handleEdit} />
                        <DotBtnItem name={'Downloaden'} onClick={handleDownload} />
                    </EditButtons> }
                </div>
            </div>
        </div>

        {
            state.isPreviewing &&
            <Modal>
                <ModalHeader>
                    <Title size='large' weight='bold'>{file.name}</Title>
                </ModalHeader>

                <br />
                <Container>
                    { fileTypesWithoutPreview.includes(file.type) && <>Er kan geen voorbeeld worden getoond van dit bestandstype.</> }
                    { fileTypesAsImage.includes(file.type) && <img src={data.blobUrl} width="100%" /> }
                    { !fileTypesWithoutPreview.includes(file.type) && 
                        !fileTypesAsImage.includes(file.type) &&
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <iframe
                                style={{
                                    width: '100%',
                                    height: '600px'
                                }}
                                src={data.blobUrl}
                                title={file.name}
                            />
                        </div>}
                </Container>
                <br />

                <ModalFooter>
                    <ButtonGroup>
                        <Button
                            variant='transparent'
                            onClick={() => setState({
                                ...state,
                                isPreviewing: false
                            })}
                        >
                            Annuleren
                        </Button>
                        <Button
                            color='primary'
                            variant='solid'
                            isLoading={state.isLoading}
                            onClick={handleDownload}
                        >
                            Downloaden
                        </Button>
                    </ButtonGroup>
                </ModalFooter>
            </Modal>
        }

        {
            state.isOpen &&
            <div className={styles['container']}>
                <RowItem>
                    <RowItemTitle>Bestandsnaam</RowItemTitle>
                    <RowItemInfo>
                        {
                            !state.isEdit
                            ? file.name
                            : <Field
                                isEdit={true}
                                value={data.name}
                                onChange={(v) => setData({...data, name: v})}
                                type={FieldType.Text}
                            />
                        }
                    </RowItemInfo>
                </RowItem>
                <RowItem>
                    <RowItemTitle>Grootte</RowItemTitle>
                    <RowItemInfo>{ Math.round(file.size / 1024) } Kb</RowItemInfo>
                </RowItem>
                <RowItem>
                    <RowItemTitle>Extension</RowItemTitle>
                    <RowItemInfo>{ convertMimeToExtension(file.type) }</RowItemInfo>
                </RowItem>
                <RowItem>
                    <RowItemTitle>Type</RowItemTitle>
                    <RowItemInfo>{ file.type }</RowItemInfo>
                </RowItem>
                <RowItem>
                    <RowItemTitle>Referentie Id</RowItemTitle>
                    <RowItemInfo>{ file.id }</RowItemInfo>
                </RowItem>
                <br />
                <small >Geüpload door {file.created_by} op {convertTimestampToDateAndTime(file.created_at.date)}</small><br/>
                <small >Laatst geupdate door {file.updated_by} op {convertTimestampToDateAndTime(file.updated_at.date)}</small>
                <br /><br />
            </div>
        }

    </div>;
}

interface Props {
    id: any
}

const mapState = (state: StoreState, props: Props) => ({
    file: getFilesByIdAsObject(state, props.id)
});

const mapDispatch = {
    patchFileById,
    deleteFileById    
};

type FileProps = Props & ReturnType<typeof mapState> & typeof mapDispatch;

export default connect(mapState, mapDispatch)(File);